import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

const initialState = {
  tasks: [],
  taskTypes: [],
  loading: false,
  error: null,
  total: 0,
  pages: 1,
};

export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/tasks', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to fetch tasks' });
    }
  }
);

export const fetchTaskTypes = createAsyncThunk(
  'tasks/fetchTaskTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/tasks/types');
      return response.data.task_types;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to fetch task types' });
    }
  }
);

export const createTask = createAsyncThunk(
  'tasks/createTask',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/tasks', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to create task' });
    }
  }
);

export const executeShell = createAsyncThunk(
  'tasks/executeShell',
  async ({ agentId, command }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/agents/${agentId}/shell`, { command });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to execute command' });
    }
  }
);

export const cancelTask = createAsyncThunk(
  'tasks/cancelTask',
  async (taskId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tasks/${taskId}/cancel`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to cancel task' });
    }
  }
);

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    taskUpdated: (state, action) => {
      const index = state.tasks.findIndex(t => t.task_id === action.payload.task_id);
      if (index !== -1) {
        state.tasks[index] = action.payload;
      }
    },
    taskAdded: (state, action) => {
      state.tasks.unshift(action.payload);
      state.total += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload.tasks;
        state.total = action.payload.total;
        state.pages = action.payload.pages;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
      })
      .addCase(fetchTaskTypes.fulfilled, (state, action) => {
        state.taskTypes = action.payload;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.tasks.unshift(action.payload);
      })
      .addCase(executeShell.fulfilled, (state, action) => {
        state.tasks.unshift(action.payload);
      })
      .addCase(cancelTask.fulfilled, (state, action) => {
        const index = state.tasks.findIndex(t => t.task_id === action.payload.task_id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
      });
  },
});

export const { taskUpdated, taskAdded } = tasksSlice.actions;
export default tasksSlice.reducer;
